import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { isAgentLogged } from 'yoda-core-components/lib/helpers/Utils/Utils';
import RestClient from '../RestClient/RestClient';
import * as OrderApiConstants from './OrderApiConstants';
import { getCookie } from '../helpers/Cookies';
import { RestConfig, getHostConfig } from '../Common/RestConfig';
import { apiHosts } from '../RestClient/FetchHelper.config';
import {
    getIrisConfiguration,
    extractUiContent,
    extractUiContentFromCM,
} from '../Common/CommonHelper';
import { getOrchestrationConfig } from './OrderApiHelper';

/*
 * Function to fetch the last order details.
 * API to fetch the information from google maps provided
 * lat long information
 */
export const getStores = (params) => {
    let config = {
        url: OrderApiConstants.STORES_URL,
        params,
        apiHost: apiHosts.browse,
        acceptVersion: '1',
        version: '/v1',
        XForwardedChannelHeaderHost: true,
    };
    config = getHostConfig(config);
    config.xClientSource = params.xClientSource;
    return RestClient.get(config).then((response) => response);
};

export const getAdjustmentDiscounts = (params) => {
    const { BASE_URL, DRAFT_ORDER, ADJUSTMENT_URL_SUFFIX } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${ADJUSTMENT_URL_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.accessToken = params.accessToken;
    return RestClient.get(config).then((json) => json);
};

export const getV2Stores = (params) => {
    const config = {
        url: OrderApiConstants.STORES_URL,
        params,
        acceptVersion: '2.1',
        version: '/v2',
    };
    return RestClient.get(config).then((response) => response);
};

export const sccQueryString = (params, url) => {
    if (params.payload.scc) {
        url += url.indexOf('?') !== -1 ? '&scc=true' : '?scc=true';
    }
    return url;
};

export const getOrderDetails = (params) => {
    const url = `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}`;
    let config = RestConfig.v1DraftConfig();
    let addB2bCustomHeaders = {};
    if (params.requestHeaderCookie) {
        const orderID = params.requestHeaderCookie.b2b_orderId;
        const isAgent = params.requestHeaderCookie.DP_USER_ROLE;
        const ACCOUNT_ID = params.accountId;

        if (isAgent && orderID) {
            addB2bCustomHeaders = {
                orderID,
                ACCOUNT_ID,
            };
        }
        config.requestHeaderCookie = params.requestHeaderCookie;
        config.geoDataHeader = params.geoDataHeader;
    }
    config = getHostConfig(config);
    config.url = url;
    config.xClientId = params.payload.clientId;
    config.accessToken = params.accessToken;
    config.xClientSource = params.payload.xClientSource;
    // B2B Voucher Portal //

    delete params.payload.clientId;
    /**
     * isAddBnplPaymentCouponHeader - needed as header and 401 retry but not needed as queryparam
     */
    const { isAddBnplPaymentCouponHeader, ...paramsPayload } = params.payload;
    config.params = paramsPayload;
    const {
        payload: { akZip = '' } = {},
        bopisInfo: { useOldCommonBopisService = true, zipCode = '' } = {},
    } = params;
    let addCustomHeaders = {};
    if (!useOldCommonBopisService && zipCode) {
        addCustomHeaders = { 'X-Geo-Location': zipCode };
    } else if (akZip) {
        addCustomHeaders = {
            'X-Geo-Location': akZip,
        };
        /**
         * Removing this when DO is 401 subsequent calls
         * 'X-Geo-Location' is not passing
         */
        // delete params.payload.akZip;
    }
    if (params.payload.xStrategy) {
        addCustomHeaders = {
            'X-STRATEGY': params.payload.xStrategy,
        };
        delete params.payload.xStrategy;
    }
    if (params.payload.isAddBnplPaymentCouponHeader) {
        addCustomHeaders = {
            'syf-payment-selected': true,
        };
    }
    /* istanbul ignore else */
    if (params.payload && params.payload.storeParams) {
        config.params = params.payload.storeParams;
    }
    if (params.elbApiHost) {
        config.host = `${params.elbApiHost}`;
    }
    config.customHeaders = { ...addB2bCustomHeaders, ...addCustomHeaders };
    return RestClient.get(config).then(/* istanbul ignore next */ (response) => response);
};

export const logDAError = (params) => {
    const url = `${OrderApiConstants.BASE_URL}/analytics-data/ping`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.accept = 'application/json';
    config.url = url;
    config.params = params.payload;
    return RestClient.post(config).then(
        /* istanbul ignore next */
        (response) => response
    );
};

export const getSubmittedOrderDetails = (params) => {
    const orderId = (params.payload || {}).orderId || getCookie('OrderId') || getCookie('DPOrder');
    const url = isEmpty(orderId)
        ? `${OrderApiConstants.ORDER_URL}/${OrderApiConstants.LAST_SUFFIX}`
        : `${OrderApiConstants.ORDER_URL}/${orderId}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.accessToken = params.accessToken;
    if (params.requestHeaderCookie) {
        config.requestHeaderCookie = params.requestHeaderCookie;
        config.geoDataHeader = params.geoDataHeader;
    }
    if (params.elbApiHost) {
        config.host = `${params.elbApiHost}`;
    }
    if ((params.payload || {}).scc) {
        config.params = { scc: true };
    }
    return RestClient.get(config).then(/* istanbul ignore next */ (json) => json);
};

export const initiateCheckout = (params) => {
    const { origin, itemsToBeDeleted, xDeleteOOSItems, xEtagValue } = params.payload;

    let url = `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}`;
    if (origin) {
        url += origin;
    }
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    /* istanbul ignore next */
    config.accept =
        params.payload.xCommand === 'submit' ||
        params.payload.xCommand === 'submit-to-agency' ||
        params.payload.xCommand === 'reject' ||
        params.payload.isVendorHeaderRequired
            ? 'application/json'
            : 'application/vnd+jcpenney.draft-order+json';
    if (isAgentLogged() && params.payload.xCommand === 'submit') {
        config.customHeaders = {
            'b2b-voucher-code': TokenProvider.get('Voucher_Number') || '',
            ACCOUNT_ID: params.accountId,
        };
    }

    /* istanbul ignore else */
    if (params.isApplePay) {
        config.contentType = 'application/vnd+jcpenney.draft-order+json';
        config.params = JSON.parse(JSON.stringify(params.payload));
        config.params.payload && delete config.params.payload.xEtagValue;
    }
    config.url = sccQueryString(params, url);
    config.accessToken = params.accessToken;
    config.isSourceTokenRequired = false;
    config.isGuestUser = false;
    config.xCommand = params.isApplePay ? params.xCommand : params.payload.xCommand;
    config.xStrategy = params.isApplePay ? params.xStrategy : params.payload.xStrategy;
    config.xClientId = params.payload.clientId;
    config.xClientSource = params.payload.xClientSource;
    if (!isEmpty(itemsToBeDeleted)) {
        config.params = { itemsToBeDeleted };
    } else if (xDeleteOOSItems) {
        config.xDeleteOOSItems = xDeleteOOSItems;
    }
    if (params.xApplePayInitiateCheckout) {
        config.xState = params.xState;
        config.xZip = params.xZip;
        config.xApplePayInitiateCheckout = params.xApplePayInitiateCheckout;
    }
    if (xEtagValue) {
        config.xEtagValue = xEtagValue;
    }
    if (params?.payload?.captchaToken) {
        if (config?.params) {
            config.params.captcha = { token: params.payload.captchaToken };
        } else {
            config.params = {
                captcha: { token: params.payload.captchaToken },
            };
        }
    }
    if (params?.payload?.accertifyDeviceInfo) {
        if (config?.params) {
            config.params.deviceInfo = params.payload.accertifyDeviceInfo;
        } else {
            config.params = {
                deviceInfo: params.payload.accertifyDeviceInfo,
            };
        }
    }

    if (!isEmpty(params?.payload?.optinData)) {
        /* istanbul ignore else */
        if (config?.params) {
            config.params.optIn = params.payload.optinData;
        } else {
            config.params = {
                optIn: params.payload.optinData,
            };
        }
    }

    return RestClient.post(config).then((response) => response);
};

export const validateMerchantSession = (params) => {
    const url = `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}`;
    let config = RestConfig.v1ApplePayConfig();
    config = getHostConfig(config);
    config.xCommand = 'initiate-apple-pay-checkout';
    config.url = url;
    config.accessToken = params.accessToken;

    return RestClient.post(config).then(
        /* istanbul ignore next */
        (response) => response
    );
};

export const initiateApplePay = (params) => {
    let config = RestConfig.v1ApplePayConfig();
    config = getHostConfig(config);
    config.xCommand = 'initiate-checkout';
    config.contentType = 'application/vnd+jcpenney.draft-order+json';
    const { shippingInfo } = params.payload;
    if (shippingInfo) {
        config.xState = shippingInfo.state;
        config.xZip = shippingInfo.zip;
        config.xCountry = shippingInfo.country;
    }
    config.accessToken = params.accessToken;
    config.xStrategy = 'apple-pay';
    config.xDeleteOOSItems = params.payload.xDeleteOOSItems;
    config.url = `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}`;
    return RestClient.post(config).then((response) => response);
};

export const submitApplePay = (params) => {
    let config = RestConfig.v1ApplePayConfig();
    config = getHostConfig(config);
    config.xCommand = 'submit';
    config.contentType = 'application/json;charset=utf-8';
    config.accessToken = params.accessToken;
    config.customHeaders = { 'x-pdot': params.payload.xpdot };
    config.url = `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}`;
    config.xStrategy = 'apple-pay';
    config.xClientId = params.payload.clientId;
    config.params = params.payload;
    if (params?.payload?.captchaToken) {
        config.params.captcha = { token: params.payload.captchaToken };
    }
    if (params?.payload?.accertifyDeviceInfo) {
        config.params.deviceInfo = params.payload.accertifyDeviceInfo;
    }
    delete params.payload.clientId;
    delete params.payload.xpdot;
    return RestClient.post(config).then((resp) => resp);
};

export const optin = (params) => {
    const config = {
        url: OrderApiConstants.OPT_IN,
        params,
        version: '/v4',
    };
    config.apiHost = apiHosts.appHost; // Forcing it over m.jcpenney.com
    return RestClient.post(config)
        .then(() => {
            const response = { subscribed: true };
            return response;
        })
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => {
                console.log(`request failed ${error}`);
                return { subscribed: false };
            }
        );
};

export const applyAdjustment = (params) => {
    /*
     * Adding logs in QM for MCYODA-18325 Increase in HTTP 401 for Coupon Discount API
     */
    if (params.accountId === undefined) {
        console.error('applyAdjustment called with undefined account id');
        return [
            {
                errorCode: 'SRV_ACCOUNT_ID_MISMATCH',
                errorMessage: 'AccountId does not match with token',
            },
        ];
    }
    const url =
        `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}` +
        `/${OrderApiConstants.ADJUSTMENT_URL_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.accessToken = params.accessToken;
    if (params.payload.captchaToken && params.payload.captchaType) {
        config.captchaToken = params.payload.captchaToken;
        config.captchaType = params.payload.captchaType;
        delete params.payload.captchaToken;
        delete params.payload.captchaType;
    }
    if (params.payload.isCaptchaEnabled && params.payload.code && params.payload.serialNumber) {
        config.customHeaders = {
            'x-restricted-coupon': true,
        };
        delete params.payload.isCaptchaEnabled;
    }
    if (params.payload.recaptchaToken) {
        config.recaptchaToken = params.payload.recaptchaToken;
        delete params.payload.recaptchaToken;
    }
    config.params = params.payload;
    config.contentType = 'application/json';
    return RestClient.post(config).then((json) => json);
};

export const getAdjustment = (params) => {
    const url =
        `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}` +
        `/${OrderApiConstants.ADJUSTMENT_URL_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.accessToken = params.accessToken;
    return RestClient.get(config).then((json) => json);
};

export const removeAdjustment = (params) => {
    const url =
        `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}` +
        `/${OrderApiConstants.ADJUSTMENT_URL_SUFFIX}/${params.payload}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.accessToken = params.accessToken;
    return RestClient.delete(config).then((json) => json);
};

export const postShippingAddress = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { DELIVERY_GROUP_SUFFIX } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${DELIVERY_GROUP_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    return RestClient.post(config).then((response) => response);
};

export const putEmailAddress = (postData) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { CONTACT } = OrderApiConstants;
    const url = `${BASE_URL}/${postData.accountId}/${DRAFT_ORDER}/${CONTACT}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = postData.payload.params;
    config.accessToken = postData.accessToken;
    return RestClient.post(config).then((response) => response);
};

export const putServiceLevels = (postData) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const DELIVERY_GROUP_SUFFIX = `${OrderApiConstants.DELIVERY_GROUP_SUFFIX}/${postData.payload.id}`;
    const SERVICE_LEVELS_SUFFIX = `${OrderApiConstants.SERVICE_LEVELS_SUFFIX}/${postData.payload.code}`;
    const url = `${BASE_URL}/${postData.accountId}/${DRAFT_ORDER}/${DELIVERY_GROUP_SUFFIX}/${SERVICE_LEVELS_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = postData.payload.params;
    config.accessToken = postData.accessToken;
    return RestClient.put(config).then((response) => response);
};

export const updateShippingGroupsInfo = (postData) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { DELIVERY_GROUP_SUFFIX } = OrderApiConstants;
    const url = `${BASE_URL}/${postData.accountId}/${DRAFT_ORDER}/${DELIVERY_GROUP_SUFFIX}/${postData.payload.id}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = postData.payload.params;
    if (postData.payload.isXStandardizationRequired) {
        config.isXStandardizationRequired = postData.payload.isXStandardizationRequired;
    }
    if (postData.payload.removeRestrictedItems) {
        config.removeRestrictedItems = postData.payload.removeRestrictedItems;
    }
    config.accessToken = postData.accessToken;
    return RestClient.put(config).then((response) => response);
};

export const updateAlternatePickupInfo = (postData) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { DELIVERY_GROUP_SUFFIX } = OrderApiConstants;
    const { PICKUP_SUFFIX } = OrderApiConstants;
    const groupId = postData.payload.id;
    const { accountId } = postData;
    const url = `${BASE_URL}/${accountId}/${DRAFT_ORDER}/${DELIVERY_GROUP_SUFFIX}/${groupId}/${PICKUP_SUFFIX}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = postData.payload.params;
    config.accessToken = postData.accessToken;
    return RestClient.put(config).then((response) => response);
};

export const getOrders = (obj) => {
    let config = RestConfig.v5AccountConfig();
    config = getHostConfig(config);
    config.url = `${OrderApiConstants.BASE_URL}/${obj.accountId}/${OrderApiConstants.ORDER_URL}`;
    config.isAuthRequired = true;
    config.accessToken = obj.accessToken;
    return RestClient.get(config).then((json) => json);
};

export const putTransactionalSMS = (params) => {
    const { BASE_URL } = OrderApiConstants;
    const { DRAFT_ORDER } = OrderApiConstants;
    const { CONTACT } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${CONTACT}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config, RestClient.getContext());
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    return RestClient.post(config).then((json) => json);
};

export const deleteTransactionalSMS = (params) => {
    const { BASE_URL } = OrderApiConstants;
    const { DRAFT_ORDER } = OrderApiConstants;
    const { CONTACT } = OrderApiConstants;
    const SMSID = params.payload && params.payload.smsId;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${CONTACT}/${SMSID}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    return RestClient.delete(config).then((json) => json);
};

export const getFinancing = (params) => {
    const url =
        `${OrderApiConstants.BASE_URL}/${params.accountId}/${OrderApiConstants.DRAFT_ORDER}` +
        `/${OrderApiConstants.BILLING}/${OrderApiConstants.CREDIT_CARDS}` +
        `/${params.payload.cardId}/${OrderApiConstants.FINANCING}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    return RestClient.get(config).catch(
        /* istanbul ignore next */
        // Ignoring this part in coverage as error can not be simulated
        (error) => ({
            error,
        })
    );
};

export const patchFinancing = (params) => {
    const { BASE_URL } = OrderApiConstants;
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BILLING } = OrderApiConstants;
    const { CREDIT_CARDS } = OrderApiConstants;
    const { FINANCING } = OrderApiConstants;
    const { OPTIONS } = OrderApiConstants;
    const url =
        `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${BILLING}/${CREDIT_CARDS}` +
        `/${params.payload.cardId}/${FINANCING}/${OPTIONS}/${params.payload.offerId}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload.params;
    config.accessToken = params.accessToken;
    return RestClient.patch(config).catch(
        /* istanbul ignore next */
        // Ignoring this part in coverage as error can not be simulated
        (error) => ({
            error,
        })
    );
};

export const postRewardOrFinancing = (params) => {
    const { BASE_URL } = OrderApiConstants;
    const { DRAFT_ORDER } = OrderApiConstants;
    const { DISCOUNTS } = OrderApiConstants;
    const { OPTIONS } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${DISCOUNTS}/${OPTIONS}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload.params;
    config.accessToken = params.accessToken;
    return RestClient.post(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const submitPayment = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/billing/credit-cards`;
    const {
        payload: { transactionToken = '' },
    } = params;
    let config = RestConfig.v1DraftConfig();
    config.url = url;
    if (params.payload.recaptchaToken) {
        config.recaptchaToken = params.payload.recaptchaToken;
        delete params.payload.recaptchaToken;
    }
    if (params.payload.hasOwnProperty('isXStandardizationRequired')) {
        config.isXStandardizationRequired = params.payload.isXStandardizationRequired;
    }
    if (transactionToken) {
        config.url += '/bnpl';
    }
    config.params = params.payload;
    if (params?.payload?.captcha?.token) {
        config.params.captcha = {
            token: params.payload.captcha.token,
        };
    }
    config.accessToken = params.accessToken;
    if (params.payload.clientId) {
        config.xClientId = params.payload.clientId;
        delete params.payload.clientId;
    }
    config = getHostConfig(config);
    return RestClient.post(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const deletePaymentMethod = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/billing/credit-cards/${params.payload.paymentId}`;
    let config = RestConfig.v1DraftConfig();
    config.url = url;

    config.params = params.payload;
    config.accessToken = params.accessToken;
    config = getHostConfig(config);
    return RestClient.delete(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

// Need to make put call to update guest payment, with credit card id
export const updateGuestPayment = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { BILLING } = OrderApiConstants;
    const { CREDIT_CARDS } = OrderApiConstants;
    const requestParam = JSON.parse(JSON.stringify(params));
    const { cardId } = requestParam.payload;

    const url = `${BASE_URL}/${requestParam.accountId}/${DRAFT_ORDER}/${BILLING}/${CREDIT_CARDS}/${cardId}`;
    let config = RestConfig.v1DraftConfig();
    config.url = url;
    delete requestParam.payload.cardId;
    if (requestParam.payload.recaptchaToken) {
        config.recaptchaToken = requestParam.payload.recaptchaToken;
        delete requestParam.payload.recaptchaToken;
    }
    if (requestParam.payload.isXStandardizationRequired) {
        config.isXStandardizationRequired = requestParam.payload.isXStandardizationRequired;
    }
    config.params = requestParam.payload;
    config.accessToken = requestParam.accessToken;
    if (params.payload.clientId) {
        config.xClientId = params.payload.clientId;
        delete params.payload.clientId;
    }
    config = getHostConfig(config);
    return RestClient.put(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

// Need to make patch call to update guest payment, with credit card id only for register user
export const updateGuestPaymentCVV = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { BILLING } = OrderApiConstants;
    const { CREDIT_CARDS } = OrderApiConstants;
    const { cardId } = params.payload;
    let configParams = '';

    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${BILLING}/${CREDIT_CARDS}/${cardId}`;
    let config = RestConfig.v1DraftConfig();
    config.url = url;
    if (params.payload.recaptchaToken) {
        config.recaptchaToken = params.payload.recaptchaToken;
    }
    if (params.payload.isXStandardizationRequired) {
        config.isXStandardizationRequired = params.payload.isXStandardizationRequired;
    }

    const cvvVal = params.payload.inputVal;
    const { addressVal } = params.payload;
    const { expiryMonthVal } = params.payload;
    const { expiryYearVal } = params.payload;
    const requestParam = {};
    requestParam.payload = [];
    requestParam.payload.push(cvvVal);
    requestParam.payload.push(addressVal);
    expiryMonthVal && requestParam.payload.push(expiryMonthVal);
    expiryYearVal && requestParam.payload.push(expiryYearVal);
    if (params?.payload?.captcha?.token) {
        configParams = {
            captcha: params.payload.captcha,
            operations: requestParam.payload,
        };
    } else {
        configParams = {
            operations: requestParam.payload,
        };
    }
    if (params?.payload?.deviceInfo) {
        configParams.deviceInfo = params?.payload?.deviceInfo;
    }
    config.params = configParams;
    config.accessToken = params.accessToken;
    if (params.payload.clientId) {
        config.xClientId = params.payload.clientId;
        delete params.payload.clientId;
    }
    config = getHostConfig(config);
    return RestClient.patch(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const submitBillingAddress = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/billing/unnamed/address`;
    let config = RestConfig.v1DraftConfig();
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    if (params.payload.isXStandardizationRequired) {
        config.isXStandardizationRequired = params.payload.isXStandardizationRequired;
    }
    config = getHostConfig(config);
    return RestClient.put(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const submitSelectedPayment = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/billing/credit-cards`;
    let config = RestConfig.v1DraftConfig();
    config.url = url;

    config.isReferenceValidHeader = true;
    config.isReferenceValid = params.payload.isReferenceValid;
    config.params = { ...params.payload };
    delete config.params.isReferenceValid;

    config.accessToken = params.accessToken;
    if (params.payload.clientId) {
        config.xClientId = params.payload.clientId;
        delete params.payload.clientId;
    }
    config = getHostConfig(config);
    return RestClient.post(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const getPayment = (params) => {
    const { DRAFT_ORDER } = OrderApiConstants;
    const { BASE_URL } = OrderApiConstants;
    const { BILLING } = OrderApiConstants;
    const { CREDIT_CARDS } = OrderApiConstants;
    const url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${BILLING}/${CREDIT_CARDS}`;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config);
    config.url = url;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    if (params.payload.clientId) {
        config.xClientId = params.payload.clientId;
        delete params.payload.clientId;
    }
    return RestClient.get(config)
        .then((json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const getMarketingSlotBanner = () => {
    let config = RestConfig.v1SearchConfig('same-origin');
    config.url = OrderApiConstants.MARKETING_SLOT_URL;
    config = getHostConfig(config);
    return RestClient.get(config)
        .then(/* istanbul ignore next */ (json) => json)
        .catch(
            /* istanbul ignore next */
            // Ignoring this part in coverage as error can not be simulated
            (error) => ({
                error,
            })
        );
};

export const updateRoundUpCharity = (params) => {
    const { BASE_URL } = OrderApiConstants;
    const { DRAFT_ORDER } = OrderApiConstants;
    const { CHARITY } = OrderApiConstants;
    let config = RestConfig.v1DraftConfig();
    config = getHostConfig(config, RestClient.getContext());
    config.url = `${BASE_URL}/${params.accountId}/${DRAFT_ORDER}/${CHARITY}`;
    config.params = params.payload;
    config.accessToken = params.accessToken;
    return RestClient.put(config).then((json) => json);
};

export const getRoundUpCharityContent = (param) => {
    let config = {};
    config = getIrisConfiguration();
    const { pageUrlOrSearchTerm = '', isCMHeaderRequired = false } = param;
    config.isCMHeaderRequired = isCMHeaderRequired;
    config.url = OrderApiConstants.IRIS_CONTENT_AGGREGATOR_URL.replace(
        /@irisSearchTerm@/g,
        pageUrlOrSearchTerm
    );
    return RestClient.get(config).then((response) => {
        const irisContent = isCMHeaderRequired
            ? extractUiContentFromCM(response)
            : extractUiContent(response);
        return {
            data: {
                urls: [
                    {
                        destinationURL: {
                            imgMap: irisContent.html,
                        },
                    },
                ],
            },
            status: response.status,
        };
    });
};

export const getPageContent = (params) => {
    let config = {};
    config = getIrisConfiguration();
    const {
        pageUrlOrSearchTerm = '',
        isCMHeaderRequired = false,
        allowMultipleDHTML = false,
    } = params;
    config.isCMHeaderRequired = isCMHeaderRequired;
    config.url =
        pageUrlOrSearchTerm.indexOf('/content-aggregator') > -1
            ? pageUrlOrSearchTerm
            : OrderApiConstants.IRIS_CONTENT_AGGREGATOR_URL.replace(
                  /@irisSearchTerm@/g,
                  pageUrlOrSearchTerm
              );
    return RestClient.get(config).then((response) => {
        const irisContent = isCMHeaderRequired
            ? extractUiContentFromCM(response, allowMultipleDHTML)
            : extractUiContent(response, allowMultipleDHTML);
        return { data: { datas: [{ data: irisContent.html }] }, status: response.status };
    });
};

export const postCurbsideCheckInInfo = (params) => {
    const config = getOrchestrationConfig(params);
    config.url = OrderApiConstants.STORE_CURBSIDE_CHECKIN_URL;
    config.isAuthRequired = true;
    config.params = get(params, 'payload');

    return RestClient.post(config).then((response) => response);
};

export const getCurbsideCheckInInfo = (params) => {
    const config = getOrchestrationConfig(params);
    config.url = OrderApiConstants.STORE_CURBSIDE_CHECKIN_URL;
    config.params = get(params, 'payload.curbsideParams', '');
    return RestClient.get(config).then((response) => response);
};

export const patchCancelCurbside = (params) => {
    const config = getOrchestrationConfig(params);
    config.url = `${OrderApiConstants.STORE_CURBSIDE_CHECKIN_URL}/${get(
        params,
        'payload.curbsideParams.id',
        ''
    )}`;
    config.params = get(params, 'payload.cancelCurbsideValue');
    return RestClient.patch(config).then((response) => response);
};

export const deleteCancelCurbside = (params) => {
    const config = getOrchestrationConfig(params);
    config.url = `${OrderApiConstants.STORE_CURBSIDE_CHECKIN_URL}/${get(
        params,
        'payload.curbsideParams.id',
        ''
    )}`;
    return RestClient.delete(config).then((response) => response);
};

/**
 * Function to get store by id without authentication.
 * @param number : storeIdOne, storeIdTwo (optional)
 * @returns {Promise.<T>|*}
 */
export const getStoreForCurbsideValidation = (obj) => {
    let config = {};
    const storeIds = obj.payload.storeId;
    config = RestConfig.v1ImpulseConfig();
    config.url = `${OrderApiConstants.MULTI_STORE}=${storeIds}`;
    config = getHostConfig(config);
    config.accessToken = obj.accessToken;
    return RestClient.get(config).then((json) => json);
};

export const getCurbsidePickupOrderInfo = (params) => {
    /** orderNumber is passing for few guestUser from EMail MCYODA-24716 */
    const search = get(location, 'search', '');
    const query = makeQuery(search);
    const orderNumber = get(query, 'orderNumber', '');
    let config = RestConfig.v1ImpulseConfig(params);
    const orderId = get(params, 'payload.curbsideParams.orderId') || orderNumber;
    const isLoggedIn = get(params, 'payload.isLoggedIn');
    config = getHostConfig(config);
    config.accessToken = get(params, 'accessToken');
    const url =
        !isLoggedIn && orderId
            ? `${OrderApiConstants.CURBSIDE_PICKUP_ORDER_URL}/${orderId}`
            : `${OrderApiConstants.CURBSIDE_PICKUP_ORDER_URL}`;
    config.url = url;
    return RestClient.get(config).then((response) => response);
};
